import { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../contexts/AuthContext';
import { updateAdminProfile, getAdminPhone } from '../lib/adminProfileApi';
import { getFirebaseAuth } from '../lib/firebase';
import {
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
} from 'firebase/auth';
import { getFirebaseAuthErrorMessage } from '../../../shared/lib/firebaseAuthMessages';

export default function PerfilPage() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showPasswordFields, setShowPasswordFields] = useState(false);

  const hasEmailPassword = user?.provider === 'email';
  const auth = getFirebaseAuth();
  const fbUser = auth?.currentUser;

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setPhone(user.phone || getAdminPhone(user.userId) || '');
    }
  }, [user]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fbUser) {
      setProfileMessage({ type: 'error', text: 'Usuário não autenticado.' });
      return;
    }
    setProfileSaving(true);
    setProfileMessage(null);
    const result = await updateAdminProfile(fbUser, { name: name.trim(), phone: phone.trim() });
    setProfileSaving(false);
    if (result.ok) {
      refreshUser();
      setProfileMessage({ type: 'success', text: 'Perfil atualizado.' });
    } else {
      setProfileMessage({ type: 'error', text: result.error ?? 'Erro ao salvar.' });
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fbUser || !auth) {
      setPasswordMessage({ type: 'error', text: 'Firebase não disponível.' });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordMessage({ type: 'error', text: 'A nova senha deve ter pelo menos 8 caracteres.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'As senhas não coincidem.' });
      return;
    }
    if (hasEmailPassword && !currentPassword.trim()) {
      setPasswordMessage({ type: 'error', text: 'Informe a senha atual.' });
      return;
    }

    setPasswordSaving(true);
    setPasswordMessage(null);

    try {
      if (hasEmailPassword && currentPassword) {
        const credential = EmailAuthProvider.credential(user?.email ?? '', currentPassword);
        await reauthenticateWithCredential(fbUser, credential);
      }
      await updatePassword(fbUser, newPassword);
      setPasswordMessage({ type: 'success', text: 'Senha alterada com sucesso.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordFields(false);
    } catch (err: unknown) {
      const msg = getFirebaseAuthErrorMessage(err, hasEmailPassword ? 'reauthenticate' : 'passwordChange');
      setPasswordMessage({ type: 'error', text: msg });
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="bg-white min-h-screen">
        <div className="sticky top-0 z-20 px-6 py-6 border-b border-gray-200 bg-white shadow-sm">
          <nav className="text-sm text-gray-500 mb-1">Home &gt; Perfil</nav>
          <h1 className="text-2xl font-bold text-gray-900">Meu perfil</h1>
        </div>

        <div className="max-w-2xl mx-auto px-6 py-8 space-y-8">
          {/* Dados básicos */}
          <section className="bg-gray-50 rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Dados básicos</h2>
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div>
                <label htmlFor="profile-email" className="block text-sm font-medium text-gray-700 mb-1">
                  E-mail
                </label>
                <input
                  id="profile-email"
                  type="email"
                  value={user?.email ?? ''}
                  disabled
                  className="w-full py-2.5 px-4 bg-gray-200 text-gray-600 rounded-lg cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500">O e-mail não pode ser alterado.</p>
              </div>
              <div>
                <label htmlFor="profile-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Nome
                </label>
                <input
                  id="profile-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Seu nome"
                  className="w-full py-2.5 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="profile-phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone
                </label>
                <input
                  id="profile-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(00) 00000-0000"
                  className="w-full py-2.5 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                />
              </div>
              <button
                type="submit"
                disabled={profileSaving}
                className="px-4 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {profileSaving ? 'Salvando...' : 'Salvar alterações'}
              </button>
              {profileMessage && (
                <p
                  className={`text-sm ${profileMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}
                >
                  {profileMessage.text}
                </p>
              )}
            </form>
          </section>

          {/* Trocar senha */}
          <section className="bg-gray-50 rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Alterar senha</h2>
            {!hasEmailPassword && (
              <p className="text-sm text-gray-600 mb-4">
                Você entrou com Google. Aqui você pode definir uma senha para login por e-mail.
              </p>
            )}
            {showPasswordFields ? (
              <form onSubmit={handleChangePassword} className="space-y-4">
                {hasEmailPassword && (
                  <div>
                    <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 mb-1">
                      Senha atual
                    </label>
                    <input
                      id="current-password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full py-2.5 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                      autoComplete="current-password"
                    />
                  </div>
                )}
                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
                    Nova senha
                  </label>
                  <input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres"
                    minLength={8}
                    className="w-full py-2.5 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirmar nova senha
                  </label>
                  <input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repita a nova senha"
                    className="w-full py-2.5 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    autoComplete="new-password"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={passwordSaving}
                    className="px-4 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {passwordSaving ? 'Alterando...' : 'Alterar senha'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordFields(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordMessage(null);
                    }}
                    className="px-4 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-100"
                  >
                    Cancelar
                  </button>
                </div>
                {passwordMessage && (
                  <p
                    className={`text-sm ${passwordMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {passwordMessage.text}
                  </p>
                )}
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setShowPasswordFields(true)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-100"
              >
                {hasEmailPassword ? 'Alterar senha' : 'Definir senha para login por e-mail'}
              </button>
            )}
          </section>
        </div>
      </div>
    </AdminLayout>
  );
}
